/**
 * Module dependencies.
 */

import { Text } from 'src/components/core/text';
import { media } from 'src/styles/media';
import { useRouter } from 'next/router';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = {
  imageUrl: string;
  imageUrlMobile?: string;
  subtitle: string;
  title: string;
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  margin-bottom: 1.25rem;

  ${media.min.md`
    margin-bottom: 5rem;
  `}
`;

/**
 * `TitleContainer` styled component.
 */

const TitleContainer = styled(Container)`
  position: relative;
  z-index: 1;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  position: relative;
  text-align: center;
  text-transform: uppercase;

  ${media.min.md`
    margin-bottom: -2.5rem;
  `}

  @media screen and (max-width: 374px) {
    font-size: 2.8rem;
    line-height: 2.8rem;
  }
`;

/**
 * `TextHandWritten` styled component.
 */

const TextHandWritten = styled(Text)`
  color: var(--color-yellow);
  display: block;
  margin: 0;
  position: relative;
  text-transform: none;
  top: -0.5rem;

  ${media.min.md`
    top: -2.5rem;
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  overflow: hidden;
  padding-bottom: 132%;
  position: relative;
  width: 100%;

  ${media.min.md`
    padding-bottom: 43.4%;
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  ${media.max.md`
    padding: 0;
  `}
`;

/**
 * Export `BioHeaderSection` component.
 */

const BioHeaderSection = (props: Props) => {
  const { imageUrl, imageUrlMobile, subtitle, title } = props;
  const isMobile = useBreakpoint('md', 'max');
  const { isReady } = useRouter();

  return (
    <Section>
      <TitleContainer>
        {isReady && (
          <FadeInUpAnimation
            options={{
              distance: '100%',
              transitionTime: 1
            }}
          >
            <Title
              as={'h1'}
              variant={'titleM'}
            >
              {title}

              <TextHandWritten
                as={'span'}
                variant={'handwrittenM'}
              >
                {subtitle}
              </TextHandWritten>
            </Title>
          </FadeInUpAnimation>
        )}
      </TitleContainer>

      <StyledContainer>
        {isReady && (
          <FadeInUpAnimation
            options={{
              distance: '25%',
              transitionTime: 1
            }}
          >
            <ImageWrapper>
              <Image
                alt={title}
                layout={'fill'}
                objectFit={'cover'}
                src={imageUrlMobile && isMobile ? imageUrlMobile : imageUrl}
              />
            </ImageWrapper>
          </FadeInUpAnimation>
        )}
      </StyledContainer>
    </Section>
  );
};

/**
 * Export `BioHeaderSection` component.
 */

export default BioHeaderSection;
